export const WEIGHT_UNITS = {
  LB: 'lb',
  OZ: 'oz',
  KG: 'kg',
  G: 'g',
} as const;

export type WeightUnit = (typeof WEIGHT_UNITS)[keyof typeof WEIGHT_UNITS];

export interface LbsOzSplit {
  readonly lbs: number;
  readonly oz: number;
}

const OZ_PER_LB = 16;
const OZ_PER_KG = 35.274;
const OZ_PER_G = 1 / 28.3495;

function roundHundredths(value: number): number {
  return Math.round(value * 100) / 100;
}

export function isWeightUnit(value: string | null | undefined): value is WeightUnit {
  return value === WEIGHT_UNITS.LB
    || value === WEIGHT_UNITS.OZ
    || value === WEIGHT_UNITS.KG
    || value === WEIGHT_UNITS.G;
}

/** Convert a stored weight + unit into ounces. Null/invalid values return null. */
export function toOunces(value: number | null | undefined, unit: string | null | undefined): number | null {
  if (value == null || !Number.isFinite(value) || value <= 0) {
    return null;
  }
  if (!isWeightUnit(unit) || unit === WEIGHT_UNITS.OZ) {
    return roundHundredths(value);
  }
  if (unit === WEIGHT_UNITS.LB) {
    return roundHundredths(value * OZ_PER_LB);
  }
  if (unit === WEIGHT_UNITS.KG) {
    return roundHundredths(value * OZ_PER_KG);
  }
  return roundHundredths(value * OZ_PER_G);
}

export function splitOunces(weightOz: number): LbsOzSplit {
  if (!(weightOz > 0) || !Number.isFinite(weightOz)) {
    return { lbs: 0, oz: 0 };
  }
  const lbs = Math.floor(weightOz / OZ_PER_LB);
  const oz = roundHundredths(weightOz - lbs * OZ_PER_LB);
  return { lbs, oz };
}

export function formatLbsOz(weightOz: number): string {
  const { lbs, oz } = splitOunces(weightOz);
  if (lbs === 0) {
    return `${oz} oz`;
  }
  const totalOz = roundHundredths(weightOz);
  return `${lbs} lb ${oz} oz, (${totalOz} oz)`;
}

export function lbsOzToOunces(lbs: number, oz: number): number {
  return roundHundredths(lbs * OZ_PER_LB + oz);
}
